import { useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles, Typography, Button, TextField, TextLink } from '@ellucian/react-design-system/core';
import {
    spacing40, spacing80,
} from '@ellucian/react-design-system/core/styles/tokens';
import { useHistory } from 'react-router-dom';
import { useApi } from '../ApiContext';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { routes } from '../Helpers/Configuration';
import { getMfaReturnUrl } from '../Helpers/fileHelpers';
import { FormattedMessage } from 'react-intl';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import LoadingButton from '../Common/LoadingButton';

const useStyles = makeStyles({
    name: 'RecoveryCode'
})((theme) => ({
    title: {
        marginBottom: spacing40
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    wrapper: {
        width: '70%',
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    recoveryCodeContainer: {
        margin: '4rem auto 4rem auto',
        width: '50%',
        [theme.breakpoints.down('lg')]: {
            width: '100%',
            margin: '0 auto'
        }
    },
    codeContainer: {
        marginBottom: spacing40,
    },
    codeTitle: {
        background:theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: spacing40
    },
    textWrapper: {
        width: '100%',
    },
    textField: {
        width: '100%'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    button: {
        marginLeft: spacing40,
        borderRadius: '0',
        border: '0'
    },
    linkContainer: {
        textAlign: 'center',
        marginTop: spacing40,
    },
    root: {
        margin:'4rem auto 0 auto',
        padding: `0 ${spacing80} 0 ${spacing80}`,
        maxWidth: '1280px',
    },
}));

const RecoveryCode = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const api = useApi();
    const snackbar = useSnackbar();
    const { formatMessage } = useIntl();
    const [backupCodeState, setBackupCodeState] = useState({ value: '' });
    const [isDisabled, setIsDisabled] = useState(false);

    const handleChange = (event) => {
        const { value } = event?.target || {};
        const error = '';
        setBackupCodeState({ value, error });
    };

    const handleSubmit = async() => {
        try{
            setIsDisabled(true);
            const userResetDetails = await api.mfaAuth.userReset(backupCodeState.value);
            const { mfaEnabled = true } = userResetDetails;
            if (!mfaEnabled) {
                window.location.href = getMfaReturnUrl() || routes.home;
            } else if (userResetDetails?.data?.isDisabled) {
                history.push(`${routes.totp}`);
            } else {
                setIsDisabled(false);
                setBackupCodeState({ value: '', error: true });
                snackbar.error(<FormattedMessage id="totpCodeIsInvalid" />);
            }
        } catch (err) {
            setIsDisabled(false);
            console.error(err);
            snackbar.error(<FormattedMessage id="totpRecoveryCodeVerificationFailed" />);
        }
    };

    const handleBack = () => {
        history.replace(routes.totp);
    };

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <div className={classes.wrapper}>
                    <div className={classes.recoveryCodeContainer}>
                        <Typography className={classes.title}>
                            <FormattedMessage id="applicationRetrievalRecoveryCodeInstructions" />
                        </Typography>

                        <div className={classes.codeContainer}>
                            <Typography className={classes.codeTitle}>
                                <HtmlFormattedMessage descriptor={{ id: 'totpEnterRecoveryCode' }} />
                            </Typography>
                            <div className={classes.textWrapper}>
                                <TextField
                                    label={formatMessage({ id: 'totpRecoveryCodeText' })}
                                    value={backupCodeState.value}
                                    type="text"
                                    error={!!backupCodeState?.error}
                                    onChange={handleChange}
                                    className={classes.textField}
                                    fullWidth
                                />
                            </div>
                        </div>

                        <div className={classes.buttonContainer}>
                            <LoadingButton
                                color="primary"
                                className={classes.button}
                                onClick={handleSubmit}
                                isLoading={isDisabled}
                                aria-label={formatMessage({ id: 'ariaLabelSubmitRecoveryCode' })}
                            >
                                <FormattedMessage id="submit" />
                            </LoadingButton>
                            <Button
                                color="secondary"
                                className={classes.button}
                                onClick={handleBack}
                                aria-label={formatMessage({ id: 'ariaLabelBack' })}
                            >
                                <FormattedMessage id="back" />
                            </Button>
                        </div>

                        <div className={classes.linkContainer}>
                            <TextLink color="secondary" onClick={()=>window.location.href = routes.totpLostRecoveryCode}><FormattedMessage id="applicationRetrievalLostRecoveryCode" /></TextLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default RecoveryCode;
